import { useRef, useState } from 'react';
import { Modal, ModalHeader, Button } from './ui';
import { DLG } from './dialogStyles';
import { removeStudentFromClass, invalidateRoster, notifyClassesChanged } from '../data/api';

/* Remove a student from Buffy's chat (BBO-82, 2026-10-13: "removing students
   and managing your classes should be completely doable through the AI").
   Same call and same consequences as Manage class → Edit students → Remove,
   including the click-again confirm: removal deletes this class's grades,
   overrides and practice progress for the student. The server checks that
   the teacher may edit this class; Buffy never removes anyone on its own. */
export default function RemoveStudentDialog({ student, auth, classId, className, onClose, onDone }) {
  const [armed, setArmed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const writing = useRef(false);
  const name = student?.name || 'this student';
  const where = className ? `from ${className}` : 'from this class';

  const remove = async () => {
    if (!armed) { setArmed(true); return; }
    if (writing.current) return;
    writing.current = true; setBusy(true); setError('');
    try {
      await removeStudentFromClass(auth, classId, student.userId);
      invalidateRoster(classId);
      notifyClassesChanged();
      onDone(`Removed ${name} ${where}.`, `Removed ${name}`);
      onClose();
    } catch (err) {
      setError(err?.viewOnly ? err.message : 'Couldn’t remove this student. Nothing changed. Try again.');
      setArmed(false);
    } finally { writing.current = false; setBusy(false); }
  };

  return (
    <Modal open onClose={busy ? () => {} : onClose} size="sm">
      <ModalHeader title={`Remove ${name}?`} sub={className || undefined} pad={DLG.head} onClose={busy ? () => {} : onClose} />
      <div className={DLG.body}>
        <p className="m-0 text-[13px] text-neutral-700 leading-relaxed">
          This deletes {name}’s grades, manual grade changes and practice progress in this class. Their own answers stay saved. If you add them back later, they start fresh here.
        </p>
        {error && <p role="alert" className="mt-[12px] mb-0 text-[12.5px] font-semibold text-error-600">{error}</p>}
      </div>
      <div className={DLG.foot}>
        <Button variant="secondary" size="dlg" onClick={onClose} disabled={busy}>Keep {name.split(' ')[0]}</Button>
        <Button variant={armed ? 'danger' : 'primary'} size="dlg" onClick={remove} disabled={busy}>
          {busy ? 'Removing…' : armed ? 'Click again to remove' : 'Remove from class'}
        </Button>
      </div>
    </Modal>
  );
}
